import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { useClient } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import { liveChallengesApi } from '@/services/liveChallenges';
import StepName from './StepName';
import StepQuestions from './StepQuestions';

const validationSchema = yup.object({
  title: yup.string().required('Título é obrigatório'),
  taxonomy_id: yup.string().required('Tema é obrigatório'),
  timer_seconds: yup.number().required('Tempo é obrigatório')
});

function buildEmptyAlternatives(questionType) {
  const labels = questionType === 'true_false' ? ['Verdadeiro', 'Falso'] : ['', '', '', ''];
  return labels.map((desc, i) => ({ description: desc, is_correct: i === 0 }));
}

function buildEmptyQuestion(position) {
  return {
    description: '',
    question_type: 'multiple_choice',
    taxonomy_id: '',
    position,
    alternatives: buildEmptyAlternatives('multiple_choice')
  };
}

export default function LiveChallengeContent({ step, id, setCurrentStep }) {
  const navigate = useNavigate();
  const client = useClient();
  const [taxonomies, setTaxonomies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isEditing = Boolean(id);

  const formik = useFormik({
    initialValues: {
      title: '',
      taxonomy_id: '',
      timer_seconds: 30,
      questions: [buildEmptyQuestion(1)]
    },
    validationSchema,
    onSubmit: async values => {
      setSubmitting(true);
      try {
        const payload = {
          title: values.title,
          taxonomy_id: values.taxonomy_id,
          timer_seconds: values.timer_seconds,
          live_challenge_questions_attributes: values.questions.map((q, idx) => ({
            description: q.description,
            question_type: q.question_type,
            taxonomy_id: q.taxonomy_id || values.taxonomy_id,
            position: idx + 1,
            live_challenge_alternatives_attributes: q.alternatives
              .filter(alt => alt.description.trim())
              .map(alt => ({
                description: alt.description.trim(),
                is_correct: alt.is_correct
              }))
          }))
        };

        if (isEditing) {
          await liveChallengesApi.update(id, payload);
          toast.success('Desafio atualizado com sucesso!');
        } else {
          await liveChallengesApi.create(payload);
          toast.success('Desafio criado com sucesso!');
        }
        navigate('/admin/desafio-ao-vivo');
      } catch (err) {
        const apiErrors = err?.response?.data?.errors;
        const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || e.message || JSON.stringify(e)).join('; ') : err?.response?.data?.message || err?.message || 'Erro ao salvar desafio.';
        toast.error(message);
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    const stepIndex = step === 'questoes' ? 2 : 1;
    setCurrentStep(stepIndex);
  }, [step, setCurrentStep]);

  useEffect(() => {
    const fetchTaxonomies = async () => {
      const { data, error } = await client.fetch('taxonomies');
      if (error) {
        toast.error('Erro ao carregar temas.');
      } else {
        setTaxonomies(data || []);
      }
    };
    fetchTaxonomies();
  }, [client]);

  useEffect(() => {
    if (!isEditing) return;
    const fetchChallenge = async () => {
      setLoading(true);
      try {
        const data = await liveChallengesApi.show(id);
        const challenge = data?.data ?? data;
        formik.setValues({
          title: challenge.title || '',
          taxonomy_id: challenge.taxonomy?.id || challenge.taxonomy_id || '',
          timer_seconds: challenge.timer_seconds || 30,
          questions:
            (challenge.questions || []).length > 0
              ? challenge.questions.map((q, idx) => ({
                  description: q.description || '',
                  question_type: q.question_type || 'multiple_choice',
                  taxonomy_id: q.taxonomy_id || '',
                  position: idx + 1,
                  alternatives: (q.alternatives || []).map(alt => ({
                    description: alt.description || '',
                    is_correct: Boolean(alt.is_correct)
                  }))
                }))
              : [buildEmptyQuestion(1)]
        });
      } catch {
        toast.error('Erro ao carregar desafio.');
      } finally {
        setLoading(false);
      }
    };
    fetchChallenge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  if (loading) return <Loader />;

  if (step === 'questoes') {
    return (
      <StepQuestions
        formik={formik}
        taxonomies={taxonomies}
        isEditing={isEditing}
        editingId={id}
        submitting={submitting}
      />
    );
  }

  return (
    <StepName
      formik={formik}
      taxonomies={taxonomies}
      isEditing={isEditing}
      editingId={id}
    />
  );
}
